import type { ReactNode } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis, Cell,
} from 'recharts';
import { formatNumber, formatShortDate, t, tx } from '../i18n';
import type { Series } from '../types/analytics';

const colors = ['#1e1e1e', '#6ba9c8', '#ff7043', '#2e7d52', '#b06000', '#7768ae'];

/** A tooltip value: numbers in the active language's format, anything else as it is. */
const formatValue = (value: unknown) => (typeof value === 'number' ? formatNumber(value) : String(value));

/** Category names that come from the API (feature names, outcomes) in the active language. */
const localizeNames = (data: Array<Record<string, string | number>>, nameKey: string) =>
  data.map((row) => (typeof row[nameKey] === 'string' ? { ...row, [nameKey]: tx(row[nameKey] as string) } : row));

export const provisionalKey = (id: string) => `${id}__provisional`;

/**
 * One row per date. Each series contributes its finalized values under its own id and its provisional values under
 * `<id>__provisional`. The last finalized point is repeated in the provisional key so the dashed segment joins the
 * solid line. A null stays null: a gap is a gap, never a zero.
 */
export function mergeSeries(series: Series[]) {
  const rows = new Map<string, Record<string, string | number | null>>();
  series.forEach((item) => item.points.forEach((point, index) => {
    const row = rows.get(point.date) ?? { date: formatShortDate(point.date) };
    const nextIsProvisional = Boolean(item.points[index + 1]?.provisional);
    row[item.id] = point.provisional ? null : point.value;
    row[provisionalKey(item.id)] = point.provisional ? point.value : nextIsProvisional ? point.value : null;
    rows.set(point.date, row);
  }));
  return [...rows.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, row]) => row);
}

export function ChartCard({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return <article className="chart-card"><header><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div></header>{children}</article>;
}

export function TrendChart({ series, valueSuffix }: { series: Series[]; valueSuffix?: string }) {
  const hasData = series.some((item) => item.points.some((point) => point.value !== null));
  if (!hasData) return <div className="chart-empty">{t('common.noChartData')}</div>;
  return (
    <div className="chart-wrap">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={mergeSeries(series)} margin={{ top: 12, right: 14, bottom: 0, left: -18 }}>
          <CartesianGrid vertical={false} stroke="#e8e3dc" />
          <XAxis dataKey="date" tickLine={false} axisLine={false} fontSize={11} minTickGap={24} />
          <YAxis tickLine={false} axisLine={false} fontSize={11} />
          <Tooltip formatter={(value) => [`${formatValue(value)}${valueSuffix || ''}`]} contentStyle={{ borderRadius: 10, borderColor: '#e8e3dc' }} />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
          {series.map((item, index) => <Line key={item.id} dataKey={item.id} name={tx(item.label)} stroke={colors[index % colors.length]} strokeWidth={2.2} dot={item.points.length === 1 ? { r: 3 } : false} connectNulls={false} />)}
          {series.filter((item) => item.points.some((point) => point.provisional)).map((item) => {
            const index = series.indexOf(item);
            return <Line key={provisionalKey(item.id)} dataKey={provisionalKey(item.id)} name={`${tx(item.label)} (${t('common.provisional')})`} stroke={colors[index % colors.length]} strokeWidth={2.2} strokeDasharray="5 4" dot={{ r: 3 }} legendType="none" connectNulls={false} />;
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function CategoryBarChart({ data, nameKey, valueKey = 'users' }: { data: Array<Record<string, string | number>>; nameKey: string; valueKey?: string }) {
  if (!data.length) return <div className="chart-empty">{t('common.noChartData')}</div>;
  return (
    <div className="chart-wrap">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={localizeNames(data, nameKey)} margin={{ top: 12, right: 12, bottom: 4, left: -14 }}>
          <CartesianGrid vertical={false} stroke="#e8e3dc" />
          <XAxis dataKey={nameKey} tickLine={false} axisLine={false} fontSize={11} interval={0} />
          <YAxis tickLine={false} axisLine={false} fontSize={11} />
          <Tooltip formatter={(value) => formatValue(value)} contentStyle={{ borderRadius: 10, borderColor: '#e8e3dc' }} />
          <Bar dataKey={valueKey} fill="#1e1e1e" radius={[6, 6, 0, 0]} maxBarSize={46} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DistributionChart({ data, nameKey, valueKey }: { data: Array<Record<string, string | number>>; nameKey: string; valueKey: string }) {
  if (!data.length) return <div className="chart-empty">{t('common.noChartData')}</div>;
  return (
    <div className="chart-wrap pie-wrap">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={localizeNames(data, nameKey)} dataKey={valueKey} nameKey={nameKey} innerRadius={54} outerRadius={82} paddingAngle={2}>
            {data.map((_, index) => <Cell key={index} fill={colors[index % colors.length]} />)}
          </Pie>
          <Tooltip formatter={(value) => formatValue(value)} contentStyle={{ borderRadius: 10, borderColor: '#e8e3dc' }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
